import React from "react";

const PricingSkeleton: React.FC = () => {
  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-[linear-gradient(160deg,#eef2fc_0%,#f5f7fd_40%,#edf1fb_70%,#f2f5fd_100%)]">
      {/* ── Atmospheric blobs ── */}
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background: `
            radial-gradient(ellipse 800px 560px at -8% -8%,  rgba(26,61,124,0.12)  0%, transparent 52%),
            radial-gradient(ellipse 700px 560px at 108% 8%,  rgba(42,91,168,0.09)  0%, transparent 52%),
            radial-gradient(ellipse 560px 440px at 52% 110%, rgba(48,205,115,0.08) 0%, transparent 52%),
            radial-gradient(ellipse 340px 260px at 80% 52%,  rgba(26,61,124,0.05)  0%, transparent 50%)
          `,
        }}
      />
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none z-0 bg-[length:52px_52px]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(26,61,124,0.035) 1px, transparent 1px),
            linear-gradient(90deg, rgba(26,61,124,0.035) 1px, transparent 1px)
          `,
        }}
      />

      <div className="relative z-[1]">
        {/* ── Hero skeleton ── */}
        <section className="py-[52px] px-6 pb-[44px] text-center">
          <div className="max-w-[620px] mx-auto flex flex-col items-center gap-5">
            {/* Badge pill */}
            <div className="animate-[ps-pulse_1.6s_ease-in-out_infinite] h-[30px] w-[280px] rounded-full bg-[rgba(255,255,255,0.70)] border-[1.5px] border-[rgba(26,61,124,0.15)]" />

            {/* H1 line 1 */}
            <div className="animate-[ps-pulse_1.6s_ease-in-out_infinite] h-[42px] w-[240px] rounded-lg bg-[rgba(15,23,42,0.10)]" />

            {/* H1 line 2 — gradient word */}
            <div className="animate-[ps-pulse_1.6s_ease-in-out_infinite] h-[50px] w-[380px] rounded-lg bg-[linear-gradient(118deg,rgba(26,61,124,0.15)_0%,rgba(48,205,115,0.12)_100%)]" />

            {/* Subtitle */}
            <div className="flex flex-col items-center gap-2 w-full">
              <div className="animate-[ps-pulse_1.6s_ease-in-out_infinite] h-3.5 w-[420px] max-w-[90%] rounded-md bg-[rgba(71,85,105,0.15)]" />
              <div className="animate-[ps-pulse_1.6s_ease-in-out_infinite] h-3.5 w-[320px] max-w-[80%] rounded-md bg-[rgba(71,85,105,0.12)]" />
            </div>

            {/* Billing toggle */}
            <div className="animate-[ps-pulse_1.6s_ease-in-out_infinite] h-[46px] w-[260px] rounded-full bg-[rgba(255,255,255,0.80)] border-[1.5px] border-[rgba(26,61,124,0.15)]" />
          </div>
        </section>

        {/* ── Cards skeleton ── */}
        <div className="px-5 pb-12 flex justify-center">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-[1100px]">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="bg-[rgba(255,255,255,0.82)] border-[1.5px] border-[rgba(26,61,124,0.10)] rounded-[20px] p-7 md:p-6 flex flex-col gap-5 backdrop-blur-[6px] shadow-[0_4px_24px_rgba(26,61,124,0.07)]"
              >
                {/* Plan name + badge */}
                <div className="flex flex-col gap-2">
                  <div className="animate-[ps-pulse_1.6s_ease-in-out_infinite] h-5 w-[100px] rounded-md bg-[rgba(26,61,124,0.12)]" />
                  <div className="animate-[ps-pulse_1.6s_ease-in-out_infinite] h-3 w-[160px] rounded bg-[rgba(26,61,124,0.08)]" />
                </div>

                {/* Price */}
                <div className="flex items-baseline gap-1.5">
                  <div className="animate-[ps-pulse_1.6s_ease-in-out_infinite] h-10 w-[90px] rounded-lg bg-[rgba(26,61,124,0.13)]" />
                  <div className="animate-[ps-pulse_1.6s_ease-in-out_infinite] h-4 w-10 rounded bg-[rgba(26,61,124,0.08)]" />
                </div>

                {/* App limit badge */}
                <div className="animate-[ps-pulse_1.6s_ease-in-out_infinite] h-[30px] w-[90px] rounded-lg bg-[rgba(26,61,124,0.09)]" />

                {/* Feature rows */}
                <div className="flex flex-col gap-3 border-t border-[rgba(26,61,124,0.08)] pt-4">
                  {[80, 65, 72, 58].map((w, j) => (
                    <div key={j} className="flex items-center gap-2.5">
                      <div className="animate-[ps-pulse_1.6s_ease-in-out_infinite] w-4 h-4 rounded-full bg-[rgba(48,205,115,0.25)] shrink-0" />
                      <div
                        className="animate-[ps-pulse_1.6s_ease-in-out_infinite] h-3 rounded bg-[rgba(26,61,124,0.09)]"
                        style={{ width: `${w}%` }}
                      />
                    </div>
                  ))}
                </div>

                {/* CTA button */}
                <div
                  className={`animate-[ps-pulse_1.6s_ease-in-out_infinite] h-[42px] w-full rounded-xl mt-auto ${
                    i === 1
                      ? "bg-[rgba(26,61,124,0.20)]"
                      : "bg-[rgba(26,61,124,0.10)]"
                  }`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* ── Trust bar skeleton ── */}
        <div className="px-6 pb-[60px] flex justify-center">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full max-w-[1000px]">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="animate-[ps-pulse_1.6s_ease-in-out_infinite] h-[46px] rounded-[14px] bg-[rgba(255,255,255,0.70)] border border-[rgba(26,61,124,0.10)]"
              />
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes ps-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.45; }
        }
      `}</style>
    </div>
  );
};

export default PricingSkeleton;
